import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import {OpenInNew} from "@mui/icons-material";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import PartnerDataGrid, {DataGridColumnPinnedTypes} from "../../../../../containers/data-grid";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import useSearch from "../../../../../hooks/use-search";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import QueryBuilder, {QueryBuilderOperatorEnum, QueryBuilderQueryConditionTypeEnum, QueryBuilderUtils} from "../../../../../../packages/query-builder";
import {createUUId} from "../../../../../../core/services/utils/utils";
import useDynamicColumns, {DynamicColumnApplications} from "../../../../../hooks/use-dynamic-columns";
import useReportingSearchUtils from "../../../../../hooks/use-reporting-search-utils";
import IconButton from "../../../../../components/base/button/icon_button";
import {Link} from "react-router-dom";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import Api from "../../../../../../core/services/api/api";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {ProductInformationTabs} from "../../products/single";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";


const queryBuilderSaveName = 'InventoryAvailabilityThresholdsQueryBuilder';
const dynamicColumnsSectionName = 'ReOrderAlert';
const dataGridSaveConfig = {
    name: "InventoryAvailabilityThresholdsDataGrid",
    version: 1,
}

const staticColumnsNames = {
    navigate: 'navigate',
    currentAvailability: 'currentAvailability',
}

const InventoryAvailabilityThresholds = ({partNo, disallowNavigation, availability: availabilityConst}) => {
    const isMounted = useIsMounted();
    const [data, setData] = useState([]);
    const [availability, setAvailability] = useState({});
    const [loadingAvailability, setLoadingAvailability] = useState({});
    const loadingAvailabilityRef = React.useRef(loadingAvailability);
    const availabilityRef = React.useRef(availability);
    const {configureNotebookTags} = useConfigureNotebook('inventory-availability-thresholds');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportAvailabilityThresholds = operatorAccessLevels.packages.bizInventory.exportAvailabilityThresholds;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.ncrList);
        configureBehavioralTool((tags) => tags.ncrList);
    }, [configureBehavioralTool, configureNotebookTags]);

    const dataGridId = useMemo(() => createUUId(true), []);
    const staticDataGridColumns = useMemo(() =>
        /** @type{DataGridColumn[]} */
        [
            {
                name: staticColumnsNames.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                name: staticColumnsNames.currentAvailability,
                title: 'Current Availability',
                type: DataGridColumnTypes.number,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                sortable: false,
                width: 140,
            }
        ].filter(e => e.name !== staticColumnsNames.navigate || !disallowNavigation), [disallowNavigation]);

    const dataGridColumnsConfig = useMemo(() => ({}), [])

    const [dynamicColumnsLoading, _queryBuilderColumns, _dataGridColumns] = useDynamicColumns({
        applicationName: DynamicColumnApplications.default,
        sectionName: dynamicColumnsSectionName,
        staticDataGridColumns: staticDataGridColumns,
        dataGridColumnsConfig: dataGridColumnsConfig,
    });

    const dataGridColumns = useMemo(() =>
        _dataGridColumns
            .filter(e => ![...(partNo ? ['partNo'] : [])].includes(e.name))
            .map((e => ({
                ...e,
                title: e.title,
            }))), [_dataGridColumns, partNo]);

    const queryBuilderColumns = useMemo(() => _queryBuilderColumns.filter(e => !partNo || e.field !== 'partNumber'), [_queryBuilderColumns, partNo]);

    const [savedDataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: dataGridColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    /**
     * Fetches the entries from the server based on the user's filters.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const siftedFilters = QueryBuilderUtils.getApiQueryFromStateQuery(args.filters, queryBuilderColumns);
        if (partNo) {
            siftedFilters.filters = siftedFilters.filters ?? [];
            siftedFilters.filters.push({
                condition: QueryBuilderQueryConditionTypeEnum.AND,
                rules: [{
                    field: 'partNumber',
                    operator: QueryBuilderOperatorEnum.equal,
                    value: partNo,
                }]
            })
        }
        const forApi = {
            reportingBaseModel: {
                keyword: siftedFilters.keyword,
                filters: siftedFilters.filters ?? [],
            },
            partNumber: partNo,
            pagination: args.paginationInfo,
            sorts: args.orderBy ? [{isDesc: args.orderBy.descending, property: args.orderBy.field,}] : [],
        }
        const response = await Api.partWarehouseReorderReport(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, queryBuilderColumns, partNo])

    /**
     * Searches among the entries based on the filters from the user.
     */
    const search = useCallback(async (args) => {
        await fetchEntries(args, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {},
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Formats the exported cell based on the column type.
     */
    const getDataGridCell = useCallback((row, column, continueMarker) => {
        switch (column.name) {
            case staticColumnsNames.navigate:
                return (
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={routeFunctions.products.single(row.id, ProductInformationTabs.availability)}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>
                )
            case staticColumnsNames.currentAvailability:
                const qty = availability[`${row.partNumber}____${row.warehouseId}`];
                return (
                    <span className={!qty ? 'text-muted' : ''}>
                        {qty ?? '-'}
                    </span>
                );
            case 'partNumber':
                return (
                    <ProductInformationPopover partNo={row.partNumber}>
                        {row.partNumber ?? ""}
                    </ProductInformationPopover>)
            default:
                return continueMarker
        }
    }, [availability])

    /**
     * Fetches the availabilities for the products shown in the grid
     * @type {(function(): Promise<void>)}
     */
    const fetchAvailabilities = useCallback(async () => {
        // 1. Extract unique partNo-warehouseId pairs
        const uniqueItemsMap = new Map();

        // Only collect items that are NOT already in availability AND NOT currently loading (checked via Ref)
        const keysToFetch = [];

        data.forEach(row => {
            if (row.partNumber && row.warehouseId) {
                const key = `${row.partNumber}____${row.warehouseId}`;
                // Dedup within this run
                if (!uniqueItemsMap.has(key)) {
                    // Check if already fetched or fetching (using Ref for immediate status)
                    if (availabilityRef.current[key] === undefined && !loadingAvailabilityRef.current[key]) {
                        uniqueItemsMap.set(key, {partNumber: row.partNumber, warehouseId: row.warehouseId});
                        keysToFetch.push(key);
                    }
                }
            }
        });

        const uniqueItems = Array.from(uniqueItemsMap.values());
        if (uniqueItems.length === 0) return;

        // Mark these items as loading immediately in both Ref and State
        setLoadingAvailability(prev => {
            const next = {...prev};
            for (const k of keysToFetch) {
                next[k] = true;
                loadingAvailabilityRef.current[k] = true;
            }
            return next;
        });

        // 2. Chunk unique items into batches of 20
        const chunkSize = 20;
        const batches = [];
        for (let i = 0; i < uniqueItems.length; i += chunkSize) {
            batches.push(uniqueItems.slice(i, i + chunkSize));
        }

        // 3. Process each batch
        for (const batch of batches) {
            const partNumbers = [...new Set(batch.map(r => r.partNumber))];
            const warehouseIds = [...new Set(batch.map(r => r.warehouseId))];
            const response = await Api.getProductAvailabilitiesV2({
                partNumbers,
                warehouseIds,
            });

            if (isMounted() && response?.resultFlag && response.data) {
                setAvailability(prev => {
                    const newAvailabilities = {};
                    // Map the response back to partNo-warehouseId pairs
                    for (const item of response.data) {
                        for (const wh of item?.localWarehouses ?? []) {
                            if (wh.warehouseId) {
                                newAvailabilities[`${item.partNumber}____${wh.warehouseId}`] = wh.availableQty;
                            }
                        }
                    }
                    availabilityRef.current = {...availabilityRef.current, ...newAvailabilities};
                    return {...prev, ...newAvailabilities};
                });

                setLoadingAvailability(prev => {
                    const next = {...prev};
                    for (const item of batch) {
                        const key = `${item.partNumber}____${item.warehouseId}`;
                        delete next[key];
                        delete loadingAvailabilityRef.current[key];
                    }
                    return next;
                });
            }
        }
    }, [data, isMounted]);

    const availabilityFromProps = React.useMemo(() => !!availabilityConst, [availabilityConst]);

    /**
     * Fetches the availabilities of the products in the grid
     */
    useEffect(() => {
        if (!data?.length || !!availabilityFromProps) return;
        void fetchAvailabilities();
    }, [availabilityFromProps, data?.length, fetchAvailabilities]);

    /**
     * If availability is provided via props, set it once on mount
     */
    useEffect(() => {
        if (availabilityFromProps) {
            const availabilitiesMap = {};
            for (const key in availabilityConst) {
                availabilitiesMap[key] = availabilityConst[key];
            }
            setAvailability(availabilitiesMap);
            availabilityRef.current = availabilitiesMap;
        }
    }, [availabilityFromProps, availabilityConst]);


    const {
        getExportInfo,
        dataGridRows,
    } = useReportingSearchUtils({
        savedDataGridColumns,
        fetchEntries,
        searchData,
        getDataGridCell,
        data,
        exportingTitle: `Inventory Availability Thresholds Report ${partNo ? `- ${partNo}` : ''}`,
    });

    return (
        <>
            <Row className="no-gutters w-100 justify-content-between align-items-center">
                {
                    !partNo &&
                    <Col xs={6}>
                        <h1 className={'text-xl font-weight-semi-bold '}>
                            Inventory Availability Thresholds
                        </h1>
                    </Col>
                }
            </Row>
            {
                dynamicColumnsLoading &&
                <>
                    <LoadingLottieAnimation
                        width={'80%'}
                        className={'my-5 py-5'}
                    />
                </>
            }
            {
                !dynamicColumnsLoading &&
                <>
                    {
                        !partNo &&
                        <QueryBuilder
                            columns={queryBuilderColumns}
                            name={queryBuilderSaveName}
                            search={(filters) => searchData.setFilters(prevState => ({...prevState, ...filters}))}
                            query={searchData.filters}
                            loading={searchData.loading}
                            dataGridId={dataGridId}
                        />
                    }
                    <div className='w-100 my-2'/>
                    <PartnerDataGrid
                        id={dataGridId}
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns: dataGridColumns}}
                        columns={savedDataGridColumns}
                        rows={dataGridRows}
                        hideFooterActions
                        state={{
                            sortBy: searchData.orderBy,
                            pagination: searchData.paginationInfo,
                            loading: {state: searchData.loading},
                            density: dataGridDensity,
                        }}
                        getExportInfo={canExportAvailabilityThresholds ? getExportInfo : undefined}
                        onSortByChanged={(_, current) =>
                            searchData.setOrderBy?.(current)}
                        onCurrentPageChanged={(_, current) =>
                            searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        onPageSizeChanged={(_, current) =>
                            searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    />
                    <Col className={'mt-4'}/>
                </>
            }
        </>
    );
};

export default InventoryAvailabilityThresholds;
